import {
  useCallback, useEffect, useState,
} from 'react';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { type DataLayerAreaValidationResult } from '~/_shared/utils/metric/dataLayer/validateDataLayerArea';
import { type BoundaryFilterRequest } from '~/spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import { type BoundaryTerritoryFilterRequest } from '~/spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import { type PolygonFilterRequest } from '~/spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { type RadiusFilterRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useFilterTreeMapSettingsParams } from '~/store/spreadsheetData/filtering/useFilterTreeMapSettingsParams';
import { createCancelToken } from '../api/api.helpers';
import { isCancelError } from '../api/apiError.helpers';
import { type MetricModel } from './metrics.factory';
import { type MetricProps } from './metrics.types';
import {
  getAndProcessMetricsData, type MetricsResults,
  type SpreadsheetMetricProps,
} from './useMetricData.helpers';

type UseMetricsDataArguments = {
  selectedMetrics: ReadonlyArray<MetricModel>;
  boundaryFilterRequest?: BoundaryFilterRequest;
  boundaryTerritoryFilterRequest?: BoundaryTerritoryFilterRequest;
  radiusFilterRequest?: RadiusFilterRequest;
  polygonFilterRequest?: PolygonFilterRequest;
  ignoreFilterTree?: boolean;
  spreadsheetRowsCount?: ReadonlyArray<SpreadsheetId>;
  validateDataLayerArea?: () => Promise<DataLayerAreaValidationResult>;
};

const emptyMetrics: SpreadsheetMetricProps = {
  rowsCount: null,
  metricList: [],
};

export const useMetricsData = ({ selectedMetrics, boundaryFilterRequest, radiusFilterRequest,
  polygonFilterRequest, boundaryTerritoryFilterRequest, ignoreFilterTree, spreadsheetRowsCount, validateDataLayerArea }: UseMetricsDataArguments) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isAgsError, setIsAgsError] = useState(false);
  const [dataLayerAreaErrorCode, setDataLayerAreaErrorCode] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [demographicMetrics, setDemographicMetrics] = useState<MetricProps[]>([]);
  const [spreadsheetColumnMetrics, setSpreadsheetColumnMetrics] = useState<SpreadsheetMetricProps>(emptyMetrics);
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const mapSettingsFilterTree = useFilterTreeMapSettingsParams();

  useEffect(() => {
    if (!clientId || !mapId) {
      return;
    }

    let isActive = true;
    setDemographicMetrics([]);
    setIsLoading(true);
    setIsError(false);
    setIsAgsError(false);
    setDataLayerAreaErrorCode(null);
    const cancelToken = createCancelToken();

    getAndProcessMetricsData({
      clientId,
      mapId,
      cancelToken: cancelToken.token,
      selectedMetrics,
      filterTreeParams: ignoreFilterTree ? undefined : mapSettingsFilterTree,
      boundaryFilterRequest,
      radiusFilterRequest,
      polygonFilterRequest,
      boundaryTerritoryFilterRequest,
      spreadsheetRowsCount,
      validateDataLayerArea,
    })
      .then((newMetrics) => {
        if (!isActive) {
          return;
        }
        setDemographicMetrics(newMetrics.demographicMetrics);
        setSpreadsheetColumnMetrics(newMetrics.spreadsheetColumnMetrics);
        setIsAgsError(newMetrics.isAgsError);
        setDataLayerAreaErrorCode(newMetrics.dataLayerAreaErrorCode);
      })
      .catch((e) => {
        if (!isActive || isCancelError(e)) {
          return;
        }
        setDemographicMetrics([]);
        setSpreadsheetColumnMetrics(emptyMetrics);
        setIsError(true);
        setIsAgsError(false);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
      cancelToken.cancel('Request canceled due to new request or component unmounting.');
    };
  }, [boundaryFilterRequest, boundaryTerritoryFilterRequest, clientId, mapId, polygonFilterRequest,
    radiusFilterRequest, selectedMetrics, ignoreFilterTree, mapSettingsFilterTree, spreadsheetRowsCount, validateDataLayerArea, refreshVersion]);

  const retryDataFetch = useCallback(() => setRefreshVersion(version => version + 1), []);

  const metricsResults: MetricsResults = {
    demographicMetrics,
    spreadsheetColumnMetrics,
  };

  return {
    metricsResults,
    isLoading,
    isError,
    isAgsError,
    dataLayerAreaErrorCode,
    retry: retryDataFetch,
  };
};
